import { get } from "lodash";
import { isContainedInList, formatDate } from "helpers/row-logic-helper";
import {
  LOAN_TYPES,
  LOAN_STATUSES,
  INTEREST_INDEXES,
  LOAN_RATE_TYPES
} from "@loanbossinc/component-library";

const loanTypeIdKeyPath = "details.loanTypeId";
const rateTypeIdKeyPath = "details.rateTypeId";

export const shouldShowProceeds = loan => isContainedInList(loan, loanTypeIdKeyPath, [
  LOAN_TYPES.CONSTRUCTION,
  LOAN_TYPES.VALUE_ADD,
  LOAN_TYPES.HUD,
  LOAN_TYPES.LINE_OF_CREDIT
]);

export const shouldShowProceedsRemaining = loan => Boolean(get(loan, "details.loanProceedsAvailable")) && isContainedInList(loan, loanTypeIdKeyPath, [
  LOAN_TYPES.CONSTRUCTION,
  LOAN_TYPES.VALUE_ADD,
  LOAN_TYPES.HUD,
  LOAN_TYPES.LINE_OF_CREDIT
]);

export const shouldShowLastModifiedDate = loan => Boolean(get(loan, "details.premodifiedLoanId"));

export const shouldShowLastLoanSpread = data => {
  const rateTypeId = get(data, rateTypeIdKeyPath);
  if (!rateTypeId) {
    return false;
  }

  const indexId = get(data, "details.indexId");
  if (!indexId) {
    return false;
  }

  return !(rateTypeId === LOAN_RATE_TYPES.FIXED && indexId === INTEREST_INDEXES.INTERNALLY_FIXED);
};

export const shouldShowLastLoanSpreadEdit = data => {
  const rateTypeId = get(data, rateTypeIdKeyPath);
  if (!rateTypeId) {
    return false;
  }

  const indexId = get(data, "details.indexId");
  if (!indexId) {
    return false;
  }

  return !(rateTypeId === LOAN_RATE_TYPES.FIXED && indexId === INTEREST_INDEXES.INTERNALLY_FIXED);
};

export const shouldShowFallbackRate = data => Boolean(get(data, "details.fallbackRate")) && isContainedInList(data, rateTypeIdKeyPath, [LOAN_RATE_TYPES.FLOATING]);

export const shouldShowFallbackRateEdit = loan => isContainedInList(loan, rateTypeIdKeyPath, [LOAN_RATE_TYPES.FLOATING]);

export const shouldShowMezzFundingStructureId = loan => isContainedInList(loan, loanTypeIdKeyPath, [LOAN_TYPES.MEZZ]);

export const shouldShowMezzRateTypeId = loan => isContainedInList(loan, loanTypeIdKeyPath, [LOAN_TYPES.MEZZ]);

export const shouldShowPaidOffDate = loan => Boolean(get(loan, "details.mezzRateTypeRef")) && isContainedInList(loan, "details.loanStatusId", [LOAN_STATUSES.PAID_OFF]);

export const shouldShowActualPrepaymentPenalty = loan => Boolean(get(loan, "details.actualPrepaymentPenalty")) && isContainedInList(loan, "details.loanStatusId", [LOAN_STATUSES.PAID_OFF]);

export const shouldShowBalanceAtPayoff = loan => !get(loan, "details.balanceAtPayoff") && isContainedInList(loan, "details.loanStatusId", [LOAN_STATUSES.PAID_OFF]);

export const shouldShowFixedIndexes = loan => isContainedInList(loan, rateTypeIdKeyPath, [LOAN_RATE_TYPES.FIXED]);

export const shouldShowFloatingIndexes = loan => isContainedInList(loan, rateTypeIdKeyPath, [LOAN_RATE_TYPES.FLOATING]);

export const getProceedsDrawnAsOfDate = data => {
  const value = get(data, "details.proceedsDrawnAsOf");
  const proceedsDrawnAsOfDate = formatDate(value);

  return proceedsDrawnAsOfDate ? `As of ${proceedsDrawnAsOfDate}` : "--";
};

export const getLastModifiedDate = data => {
  const value = get(data, "details.lastModifiedDate");
  const lastModifiedDate = formatDate(value, "MMMM DD, YYYY");

  return lastModifiedDate ? `Modified on ${lastModifiedDate}` : null;
};

export const getClosingCostDateIsAssumed = data => (get(data, "details.isClosingDateAssumed") ? "Assumed" : null);

export const shouldShowRateEdit = data => isContainedInList(data, rateTypeIdKeyPath, [LOAN_RATE_TYPES.FIXED]);

export const shouldShowRate = data => Boolean(get(data, "details.interestRate")) && isContainedInList(data, rateTypeIdKeyPath, [LOAN_RATE_TYPES.FIXED]);

export const shouldShowLoanAmount = loan => Boolean(get(loan, "details.amount"));

export const shouldShowCurrentPrincipalBalance = loan => Boolean(get(loan, "details.currentPrincipalBalance"));

export const shouldShowNextPayment = loan => Boolean(get(loan, "details.currentPayment"));

export const shouldShowClosingDate = loan => Boolean(get(loan, "details.closingDate"));

export const shouldShowMaturityDate = loan => Boolean(get(loan, "details.maturityDate"));

export const shouldShowExtendedMaturityDate = loan => Boolean(get(loan, "details.extendedMaturityDate"));

export const shouldShowAllInRate = loan => Boolean(get(loan, "details.allInRate"));

export const shouldShowIndex = loan => Boolean(get(loan, "details.index.name"));

export const shouldShowRateTypeId = loan => Boolean(get(loan, "details.rateTypeRef.name"));

export const shouldShowDaycount = loan => Boolean(get(loan, "details.daycount"));

export const isLoanFloatingRateType = loan => isContainedInList(loan, rateTypeIdKeyPath, [
  LOAN_RATE_TYPES.FLOATING
]);

export const shouldShowConversionOption = loan => Boolean(get(loan, "details.conversionOption"));
